import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, useMap } from 'react-leaflet';
import { Search, X, RectangleHorizontalIcon, Ruler, LayoutGrid, MapPin, ArrowRight, Minus } from 'lucide-react';
import { FORMATS } from '../config/prices';
import EditableRectangle from './EditableRectangle';
import L from 'leaflet';
import { useToast } from "./use-toast";
import polyline from '@mapbox/polyline';
import { getActivityTypeColor } from '../utils/activityColor';
import { Button } from "./button"

const HIGHLIGHT_COLOR = '#ff3b3b';

const MapController = ({ rectangleBounds, shouldZoomToRectangle, setShouldZoomToRectangle }) => {
  const map = useMap();
  useEffect(() => {
    if (shouldZoomToRectangle && rectangleBounds) {
      const bounds = L.latLngBounds(rectangleBounds);
      map.fitBounds(bounds, { padding: [50, 50] });
      setTimeout(() => setShouldZoomToRectangle(false), 0);
    }
  }, [shouldZoomToRectangle]);

  return null;
};

// Imperatively fit the map to the rectangle / a selected activity's polyline.
// Child of MapContainer so it has access to the leaflet map instance via
// useMap; the parent signals via a nonce that increments on each click.
// Notifies the parent whenever the leaflet map's zoom changes, so Dashboard
// can persist the current zoom and restore it across reloads. Uses a ref
// for the callback so we don't rebind the leaflet listener every render.
const ZoomListener = ({ onZoomChange }) => {
  const map = useMap();
  const cbRef = useRef(onZoomChange);
  useEffect(() => { cbRef.current = onZoomChange; }, [onZoomChange]);
  useEffect(() => {
    const handler = () => { if (cbRef.current) cbRef.current(map.getZoom()); };
    map.on('zoomend', handler);
    return () => map.off('zoomend', handler);
  }, [map]);
  return null;
};

// Imperatively restores the map view to the parent-supplied center + zoom
// when `nonce` increments. MapContainer only reads center/zoom at mount,
// so settings restored asynchronously need this nudge to actually move
// the map without tearing down the component.
const ViewRestorer = ({ mapCenter, zoom, nonce }) => {
  const map = useMap();
  useEffect(() => {
    if (nonce <= 0) return;
    if (!Array.isArray(mapCenter) || mapCenter.length !== 2) return;
    map.setView(mapCenter, typeof zoom === 'number' ? zoom : map.getZoom(), { animate: false });
    // Intentionally omit mapCenter/zoom from deps: we only want to restore on
    // an explicit nonce bump (e.g. after loadSettings), not every pan/zoom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);
  return null;
};

const ImperativeZoom = ({ rectangleBounds, zoomToRectangleNonce, centerOnCoords, centerNonce }) => {
  const map = useMap();

  useEffect(() => {
    if (zoomToRectangleNonce > 0 && rectangleBounds) {
      map.fitBounds(L.latLngBounds(rectangleBounds), { padding: [50, 50] });
    }
  }, [zoomToRectangleNonce]);

  useEffect(() => {
    if (centerNonce > 0 && centerOnCoords && centerOnCoords.length >= 2) {
      map.fitBounds(L.latLngBounds(centerOnCoords), { padding: [60, 60] });
    }
  }, [centerNonce]);

  return null;
};

const isValidCoordinate = (coord) => {
  return Array.isArray(coord) &&
    coord.length === 2 &&
    !isNaN(coord[0]) &&
    !isNaN(coord[1]) &&
    coord[0] >= -90 &&
    coord[0] <= 90 &&
    coord[1] >= -180 &&
    coord[1] <= 180;
};

// Observes the outer map container's size and forces leaflet to recompute
// its internal dimensions when it changes (otherwise the new area renders
// gray until the user pans). After invalidating, we re-fit the viewport to
// the print-area rectangle with a small margin — this keeps the rectangle
// visually anchored AND lets the map use the new available space (zoom in
// when the panel collapses, zoom out when it expands).
const AutoInvalidateSize = ({ rectangleBounds }) => {
  const map = useMap();
  const boundsRef = useRef(rectangleBounds);
  useEffect(() => { boundsRef.current = rectangleBounds; }, [rectangleBounds]);

  useEffect(() => {
    const container = map.getContainer();
    if (!container || typeof ResizeObserver === 'undefined') return;
    let firstObservation = true;
    const observer = new ResizeObserver(() => {
      map.invalidateSize({ pan: false, debounceMoveend: true });
      // Skip the very first ResizeObserver fire (initial mount); the map's
      // own initial setView already framed the rectangle correctly, and
      // calling fitBounds here would override the user's saved zoom.
      if (firstObservation) {
        firstObservation = false;
        return;
      }
      const b = boundsRef.current;
      if (
        Array.isArray(b) && b.length === 2 &&
        Array.isArray(b[0]) && Array.isArray(b[1]) &&
        b.flat().every((n) => typeof n === 'number' && !Number.isNaN(n))
      ) {
        map.fitBounds(L.latLngBounds(b), { padding: [50, 50] });
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);
  return null;
};

const ActivityFetchHandler = ({ onFetchActivities }) => {
  const map = useMap();
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleMoveEnd = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onFetchActivities(map.getBounds());
      }, 500);
    };

    onFetchActivities(map.getBounds());
    map.on('moveend', handleMoveEnd);
    map.on('zoomend', handleMoveEnd);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      map.off('moveend', handleMoveEnd);
      map.off('zoomend', handleMoveEnd);
    };
  }, [map, onFetchActivities]);

  return null;
};

// Re-centers the map when the user searches for a new place name. Uses the
// OpenStreetMap Nominatim endpoint; we only read lat/lon, no polygon/border
// overlay is drawn on the map anymore.
const PlaceCenterer = ({ placeName, shouldCenter, setShouldCenter }) => {
  const map = useMap();
  useEffect(() => {
    if (!shouldCenter || !placeName || placeName.length < 2) return;

    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(placeName)}&format=json`
        );
        const results = await response.json();
        if (cancelled || !results?.length) return;
        const lat = parseFloat(results[0].lat);
        const lon = parseFloat(results[0].lon);
        map.setView([lat, lon], map.getZoom());
        setShouldCenter(false);
      } catch (_err) {
        // Non-fatal: ignore lookup failures; user can still pan manually.
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [placeName, shouldCenter, setShouldCenter, map]);

  return null;
};

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

const TILE_STYLES = {
  standard: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; OpenStreetMap contributors',
  },
  topo: {
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution: '&copy; OpenTopoMap (CC-BY-SA)',
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: '&copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics',
  },
};

const MapComponent = ({
  mapCenter,
  zoom,
  rectangleBounds,
  setRectangleBounds,
  km2,
  selectedFormat,
  placeName,
  onMapCenterChange,
  shouldCenter,
  setShouldCenter,
  shouldZoom,
  onZoomChange,
  restoreViewNonce = 0,
  activitySettings = {},
  showActivityPolylines = true,
  activitySources = ['strava', 'garmin', 'manual'],
  hiddenActivityIds = [],
  onActivitiesChange,
  activityDateFrom,
  activityDateTo,
  activityAllDates = true,
  includeCommutes = false,
  mapStyle = 'standard',
  onMapStyleChange,
  compact = false,
  onKm2Change,
  onFormatSelect,
  isPortrait = false,
  onTogglePortrait,
  onPlaceNameChange,
  searchResults = [],
  onSelectResult,
  recentPlaces = [],
  onSelectRecent,
  onClearPlace,
  onCenterPlace,
  onActivityLineWidthChange,
  onActivityLineWidthAutoChange,
}) => {
  const validCenter = isValidCoordinate(mapCenter) ? mapCenter : [0, 0];

  // Initial auto-fit was removed: the map opens on whatever `mapCenter`/`zoom`
  // it receives, and is only re-fit to the rectangle when the user clicks the
  // rectangle button (ImperativeZoom via nonce). Keeping MapController for
  // back-compat with the `shouldZoom` prop flow, but initial state is false.
  const [shouldZoomToRectangle, setShouldZoomToRectangle] = useState(false);
  const [activityPolylines, setActivityPolylines] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const activityErrorShownRef = useRef(false);
  const { toast } = useToast();


  // Nonces drive the imperative zoom effects inside MapContainer. We bump
  // them on button clicks instead of using a boolean flag so repeated clicks
  // with identical state still trigger a re-zoom.
  const [zoomToRectNonce, setZoomToRectNonce] = useState(0);
  const [centerNonce, setCenterNonce] = useState(0);
  const [centerCoords, setCenterCoords] = useState(null);

  const [finderOpen, setFinderOpen] = useState(false);
  const [finderQuery, setFinderQuery] = useState('');
  const [finderTypeFilter, setFinderTypeFilter] = useState(null);
  // Persist the highlighted activity across reloads so a user mid-inspection
  // doesn't lose their place. Restoring an id that's no longer in the bounds
  // is harmless — the renderers below find-by-id and silently no-op when it
  // returns undefined.
  const [highlightedActivityId, setHighlightedActivityId] = useState(() => {
    try {
      const raw = localStorage.getItem('highlightedActivityId');
      if (raw == null || raw === 'null') return null;
      const parsed = JSON.parse(raw);
      return parsed === '' ? null : parsed;
    } catch (_e) {
      return null;
    }
  });
  useEffect(() => {
    try {
      if (highlightedActivityId == null) {
        localStorage.removeItem('highlightedActivityId');
      } else {
        localStorage.setItem('highlightedActivityId', JSON.stringify(highlightedActivityId));
      }
    } catch (_e) { /* quota / private mode — no-op */ }
  }, [highlightedActivityId]);

  // The thick red highlight is only meant as a transient "yes, that's the
  // one" visual — keeping it on permanently competes with the rest of the
  // map. After 5s we drop the emphasis but leave the activity selected so
  // the corner buttons still work.
  const [highlightVisible, setHighlightVisible] = useState(false);
  const highlightTimerRef = useRef(null);
  useEffect(() => {
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    if (highlightedActivityId && highlightVisible) {
      highlightTimerRef.current = setTimeout(() => {
        setHighlightVisible(false);
        highlightTimerRef.current = null;
      }, 5000);
    }
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, [highlightedActivityId, highlightVisible]);

  // Marks the location pill's center-arrow as "ready to fire" after the user
  // picks a suggestion or a recent place. Reset once they click it (or clear
  // the place). Purely cosmetic — draws attention to the next step.
  const [pendingCenter, setPendingCenter] = useState(false);
  useEffect(() => {
    if (!placeName) setPendingCenter(false);
  }, [placeName]);

  // In-map popups for the size (km²), format, and location controls, so the
  // user doesn't have to leave the map view to adjust them.
  const [sizePopupOpen, setSizePopupOpen] = useState(false);
  const [formatPopupOpen, setFormatPopupOpen] = useState(false);
  const [locationPopupOpen, setLocationPopupOpen] = useState(false);
  const [lineWidthPopupOpen, setLineWidthPopupOpen] = useState(false);

  // Local-only state for the search input inside the location popup. We
  // deliberately don't seed it from `placeName` — once the user has
  // centered on a place (or reloaded the page), the input should be a
  // blank canvas for the next search rather than preserving the previous
  // query, which mismatches reality after a manual map drag.
  const [searchInput, setSearchInput] = useState('');

  // Visible (= unhidden) activity count drives both the auto width
  // suggestion and the popup info row. Hidden ids come from the activity
  // filter panel (per-type/per-id toggles), so toggling there feeds back
  // into the auto width.
  const visibleActivityCount = useMemo(() => {
    if (!hiddenActivityIds || hiddenActivityIds.length === 0) return activityPolylines.length;
    const hidden = new Set(hiddenActivityIds);
    let n = 0;
    for (const a of activityPolylines) if (!hidden.has(a.id)) n += 1;
    return n;
  }, [activityPolylines, hiddenActivityIds]);

  // In auto mode, the activity line width tracks the visible activity count
  // — single-track maps render bold, crowded ones go thin. We re-apply the
  // suggestion whenever the count changes or auto is freshly toggled on.
  // Skips redundant updates when the suggested value is already in place to
  // avoid render loops. In manual mode the user's value is left alone.
  const lineWidthAuto = activitySettings?.lineWidthAuto === true;
  useEffect(() => {
    if (!onActivityLineWidthChange) return;
    if (!lineWidthAuto) return;
    const count = visibleActivityCount;
    const suggested = count <= 0
      ? 0.8
      : Math.max(0.05, Math.round((2.4 / Math.pow(count, 0.4)) * 100) / 100);
    const currentRaw = activitySettings?.lineWidth;
    if (typeof currentRaw === 'number' && currentRaw === suggested) return;
    onActivityLineWidthChange(suggested);
  }, [visibleActivityCount, lineWidthAuto, activitySettings?.lineWidth, onActivityLineWidthChange]);

  // Outside-click dismiss. Each ref wraps its trigger (and sometimes its
  // popup); a mousedown outside all of them closes whichever panel is open.
  const finderRef = useRef(null);
  const sizeRef = useRef(null);
  const formatRef = useRef(null);
  const locationRef = useRef(null);
  const lineWidthRef = useRef(null);

  useEffect(() => {
    if (!finderOpen && !sizePopupOpen && !formatPopupOpen && !locationPopupOpen && !lineWidthPopupOpen) return;
    const handleDown = (e) => {
      const insideAny = [finderRef, sizeRef, formatRef, locationRef, lineWidthRef].some(
        (r) => r.current && r.current.contains(e.target)
      );
      if (insideAny) return;
      setFinderOpen(false);
      setSizePopupOpen(false);
      setFormatPopupOpen(false);
      setLocationPopupOpen(false);
      setLineWidthPopupOpen(false);
    };
    document.addEventListener('mousedown', handleDown);
    return () => document.removeEventListener('mousedown', handleDown);
  }, [finderOpen, sizePopupOpen, formatPopupOpen, locationPopupOpen, lineWidthPopupOpen]);

  useEffect(() => {
    setShouldZoomToRectangle(shouldZoom);
  }, [shouldZoom]);

  const handleZoomToRectangle = () => {
    setZoomToRectNonce((n) => n + 1);
  };

  const handleSelectActivity = (activity) => {
    setHighlightedActivityId(activity.id);
    setCenterCoords(activity.coordinates);
    setCenterNonce((n) => n + 1);
    setFinderOpen(false);
    setHighlightVisible(true);
    handleFitToActivity(activity);
  };

  // Resize the print-area rectangle to fit a given activity's bounding box
  // (with a small margin) and flip the portrait flag to match its dominant
  // axis. When called without an explicit activity, falls back to the
  // currently highlighted one. No-op if the parent doesn't pass `onKm2Change`.
  const handleFitToActivity = (explicitActivity) => {
    const activity = explicitActivity
      || activityPolylines.find((a) => a.id === highlightedActivityId);
    if (!activity || !Array.isArray(activity.coordinates) || activity.coordinates.length < 2) return;
    if (!onKm2Change) return;

    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const [lat, lng] of activity.coordinates) {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    }

    // Approximate meters per degree — good enough at print-area scales where
    // the activity spans a few km. Avoids pulling in turf just for this.
    const avgLat = (minLat + maxLat) / 2;
    const metersPerDegLat = 111320;
    const metersPerDegLng = 111320 * Math.cos((avgLat * Math.PI) / 180);
    const activityWidthM = (maxLng - minLng) * metersPerDegLng;
    const activityHeightM = (maxLat - minLat) * metersPerDegLat;

    // Orientation: match the print-area's long axis to the activity's.
    const activityIsPortrait = activityHeightM > activityWidthM;
    const willBePortrait = onTogglePortrait ? activityIsPortrait : isPortrait;
    if (onTogglePortrait && isPortrait !== activityIsPortrait) onTogglePortrait();

    // Compute the smallest km² such that the rectangle (in the NEW
    // orientation) covers the activity bbox with a 10% margin. The
    // rectangle is width = sqrt(area * R), height = sqrt(area / R) where
    // R = aspectRatio in the new orientation.
    const baseAR = selectedFormat?.aspectRatio || 4 / 3;
    // selectedFormat.aspectRatio already reflects the current orientation;
    // if we're flipping, invert it for the calculation.
    const R = willBePortrait === isPortrait ? baseAR : 1 / baseAR;
    const margin = 1.1;
    const targetW = activityWidthM * margin;
    const targetH = activityHeightM * margin;
    const areaByWidth = (targetW * targetW) / R;
    const areaByHeight = targetH * targetH * R;
    const areaM2 = Math.max(areaByWidth, areaByHeight);
    const km2 = Math.max(1, Math.min(20000, Math.ceil(areaM2 / 1e6)));

    if (onKm2Change) onKm2Change(km2);
    // Map center is already on (or near) the activity from the Activity
    // finder click that ran before this; EditableRectangle will recompute
    // bounds around the current center once km²/aspectRatio change.
  };

  // Unique activity types currently visible on the map, sorted by count
  // (most common first). Drives the category chips in the finder popup.
  const finderCategories = useMemo(() => {
    const counts = new Map();
    for (const a of activityPolylines) {
      if (hiddenActivityIds.includes(a.id)) continue;
      const t = a.type || 'Other';
      counts.set(t, (counts.get(t) || 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({ type, count }));
  }, [activityPolylines, hiddenActivityIds]);

  const filteredFinderActivities = useMemo(() => {
    const q = finderQuery.trim().toLowerCase();
    let pool = activityPolylines.filter((a) => !hiddenActivityIds.includes(a.id));
    // A chosen category hides everything else, per the user's spec: clicking
    // a category button collapses the list to just that type.
    if (finderTypeFilter) {
      pool = pool.filter((a) => (a.type || 'Other') === finderTypeFilter);
    } else if (q) {
      pool = pool.filter(
        (a) =>
          (a.name || '').toLowerCase().includes(q) ||
          (a.type || '').toLowerCase().includes(q)
      );
    } else {
      // With no category picked and no query, show nothing until the user
      // narrows the list — avoids dumping hundreds of rows into the popup.
      return [];
    }
    // Newest first when a date is present.
    return pool
      .slice()
      .sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''))
      .slice(0, 200);
  }, [finderQuery, finderTypeFilter, activityPolylines, hiddenActivityIds]);

  const formatActivityDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Extracted because the finder popup is now rendered anchored to its
  // trigger button inside the right-side column, not as a top-level sibling.
  const renderFinderContent = () => (
    <>
      {finderTypeFilter ? (
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <span className="text-sm font-medium text-gray-800">{finderTypeFilter}</span>
          <button
            type="button"
            onClick={() => setFinderTypeFilter(null)}
            className="text-xs text-gray-500 hover:text-gray-900 p-0"
          >
            ← categories
          </button>
        </div>
      ) : (
        <>
          <div className="px-3 pt-3 pb-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">
              Filter by type
            </div>
            {finderCategories.length === 0 ? (
              <div className="text-sm text-gray-500">No activities in view.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {finderCategories.map(({ type, count }) => {
                  const color = getActivityTypeColor(type, activitySettings);
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        setFinderTypeFilter(type);
                        setFinderQuery('');
                      }}
                      className="text-xs px-2.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 border border-transparent flex items-center gap-1.5"
                    >
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                      {type}
                      <span className="text-gray-500">({count})</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 px-3 py-1">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-[10px] uppercase tracking-wider text-gray-400">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <div className="px-3 pb-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={finderQuery}
                onChange={(e) => setFinderQuery(e.target.value)}
                placeholder="Search by name…"
                autoFocus
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-gray-400"
              />
            </div>
          </div>
        </>
      )}

      <div className="overflow-y-auto flex-1 border-t border-gray-100">
        {!finderTypeFilter && !finderQuery.trim() ? (
          <div className="p-3 text-xs text-gray-400 text-center">
            Pick a category or start typing.
          </div>
        ) : filteredFinderActivities.length === 0 ? (
          <div className="p-3 text-sm text-gray-500">No activities match.</div>
        ) : (
          filteredFinderActivities.map((a) => {
            const color = getActivityTypeColor(a.type, activitySettings);
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => handleSelectActivity(a)}
                className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 hover:bg-gray-50 flex items-start gap-2 ${highlightedActivityId === a.id ? 'bg-yellow-50' : 'bg-white'}`}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate text-gray-900">{a.name || '(unnamed activity)'}</div>
                  <div className="text-xs text-gray-500">
                    {formatActivityDate(a.startDate) || (a.type || 'Other')}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </>
  );

  const activeStyle = TILE_STYLES[mapStyle] ? mapStyle : 'standard';
  const { url: tileUrl, attribution: tileAttribution } = TILE_STYLES[activeStyle];

  const handleCenterChange = (newCenter) => {
    if (onMapCenterChange) onMapCenterChange(newCenter);
  };

  const resolveWeight = (lineWidth) => {
    if (typeof lineWidth === 'number') return lineWidth;
    if (lineWidth === 'very thin') return 0.5;
    if (lineWidth === 'thin') return 0.75;
    if (lineWidth === 'thick') return 3;
    if (lineWidth === 'very thick') return 5;
    // Legacy "normal" / unknown strings fall through to the same default
    // first-time users get, so the preview matches the chip on both
    // desktop and mobile (no viewport-based scaling — the user-picked
    // numeric value is the source of truth on every device).
    return 0.8;
  };

  const sourcesKey = activitySources.join(',');
  // Push the picker-derived dates to start/end-of-day so an activity later
  // today still falls inside a `to=today` filter — matches the bumping in
  // dashboard.js:dateBoundaryISO.
  const _dayBoundary = (d, kind) => {
    if (!d) return '';
    const out = new Date(d);
    if (kind === 'end') out.setHours(23, 59, 59, 999);
    else out.setHours(0, 0, 0, 0);
    return out.toISOString();
  };
  const dateFromKey = activityAllDates ? '' : _dayBoundary(activityDateFrom, 'start');
  const dateToKey = activityAllDates ? '' : _dayBoundary(activityDateTo, 'end');
  const commutesKey = includeCommutes ? '1' : '0';

  const fetchActivitiesInBounds = useCallback(
    async (bounds) => {
      if (!showActivityPolylines) return;
      if (!sourcesKey) {
        setActivityPolylines([]);
        if (onActivitiesChange) onActivitiesChange([]);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) return;

      setIsLoadingActivities(true);
      try {
        const body = {
          bounds: {
            minLat: bounds.getSouth(),
            maxLat: bounds.getNorth(),
            minLon: bounds.getWest(),
            maxLon: bounds.getEast(),
          },
          margin_km: 100,
          sources: sourcesKey.split(','),
          include_commutes: commutesKey === '1',
        };
        if (dateFromKey && dateToKey) {
          body.date_range = { start: dateFromKey, end: dateToKey };
        }
        const response = await fetch(`${API_URL}/user/activities/polylines`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();
        // Skip entries that decode to fewer than 2 points — drawing a polyline
        // from them is meaningless and they shouldn't clutter the list.
        const decoded = data.activities
          .map(activity => {
            const coords = activity.polyline
              ? polyline.decode(activity.polyline).map(([lat, lng]) => [lat, lng])
              : [];
            return {
              id: activity.id,
              name: activity.name,
              type: activity.type,
              coordinates: coords,
              distance: activity.distance,
              elevation: activity.total_elevation_gain,
              startDate: activity.start_date,
            };
          })
          .filter(a => a.coordinates.length >= 2);

        setActivityPolylines(decoded);
        if (onActivitiesChange) onActivitiesChange(decoded);
        activityErrorShownRef.current = false;
      } catch (error) {
        console.error('Error fetching activities:', error);
        if (!activityErrorShownRef.current) {
          activityErrorShownRef.current = true;
          toast({
            title: "Couldn't load activities",
            description: "Your activity overlay may be out of date.",
            variant: "destructive",
            duration: 3000,
          });
        }
      } finally {
        setIsLoadingActivities(false);
      }
    },
    [showActivityPolylines, sourcesKey, dateFromKey, dateToKey, commutesKey, toast, onActivitiesChange]
  );

  const hiddenIdSet = new Set(hiddenActivityIds);

  // Mobile gets a taller viewport-based height so the map dominates the
  // screen; desktop keeps its aspect ratio to preserve the print-preview
  // framing alongside the sidebar.
  const desktopAspect = compact ? 'lg:aspect-[4/3]' : 'lg:aspect-[3/2]';
  const mobileHeight = 'h-[70vh] lg:h-auto';

  return (
    <div className="space-y-4">
      <div className={`relative w-full ${mobileHeight} ${desktopAspect} border-4 lg:border-8 border-black rounded`}>
        <MapContainer
          center={validCenter}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
          scrollWheelZoom={false}
        >
          <TileLayer url={tileUrl} attribution={tileAttribution} />

          {showActivityPolylines && activityPolylines
            .filter(activity => !hiddenIdSet.has(activity.id))
            .map(activity => (
              <Polyline
                key={activity.id}
                positions={activity.coordinates}
                pathOptions={{
                  // Match the per-type checkbox tick color in ActivityFilterPanel
                  // so the map is visually tied to the filter list. Line width
                  // and opacity stay on the common settings — per-type values
                  // only take effect in the generated image.
                  color: getActivityTypeColor(activity.type, activitySettings),
                  weight: resolveWeight(activitySettings?.lineWidth),
                  opacity: activitySettings?.alpha != null ? activitySettings.alpha : 0.8,
                }}
              />
            ))}

          {/* Highlighted activity drawn last so it sits on top of the regular
              polylines; distinct color + thicker weight makes it pop. Only
              shown for ~5s after selection — see the highlightVisible timer. */}
          {highlightedActivityId && highlightVisible && (() => {
            const a = activityPolylines.find((x) => x.id === highlightedActivityId);
            if (!a || a.coordinates.length < 2) return null;
            return (
              <Polyline
                positions={a.coordinates}
                pathOptions={{ color: HIGHLIGHT_COLOR, weight: 5, opacity: 1 }}
              />
            );
          })()}

          <EditableRectangle
            bounds={rectangleBounds}
            setBounds={setRectangleBounds}
            km2={km2}
            aspectRatio={selectedFormat.aspectRatio}
            onCenterChange={handleCenterChange}
          />

          <MapController
            rectangleBounds={rectangleBounds}
            shouldZoomToRectangle={shouldZoomToRectangle}
            setShouldZoomToRectangle={setShouldZoomToRectangle}
          />
          <ImperativeZoom
            rectangleBounds={rectangleBounds}
            zoomToRectangleNonce={zoomToRectNonce}
            centerOnCoords={centerCoords}
            centerNonce={centerNonce}
          />
          <ViewRestorer
            mapCenter={mapCenter}
            zoom={zoom}
            nonce={restoreViewNonce}
          />
          <ZoomListener onZoomChange={onZoomChange} />
          <PlaceCenterer
            placeName={placeName}
            shouldCenter={shouldCenter}
            setShouldCenter={setShouldCenter}
          />
          {showActivityPolylines && (
            <ActivityFetchHandler onFetchActivities={fetchActivitiesInBounds} />
          )}
          <AutoInvalidateSize rectangleBounds={rectangleBounds} />
        </MapContainer>

        {isLoadingActivities && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white p-2 rounded shadow-md text-sm flex items-center gap-2 z-[1000]">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            Loading activities...
          </div>
        )}

        {/* Top-right stack: km², format, and the activity finder — stacked
            vertically in that order. Each trigger opens a popup anchored
            below itself via `top-full right-0`. */}
        <div className="absolute top-2 right-2 lg:top-4 lg:right-4 z-[1000] flex flex-col gap-1 lg:gap-2 items-end">
          {onKm2Change && (
            <div className="relative" ref={sizeRef}>
              <button
                type="button"
                onClick={() => {
                  setSizePopupOpen((v) => !v);
                  setFormatPopupOpen(false);
                }}
                title="Change area size"
                className={`rounded shadow-md px-1.5 py-0.5 lg:px-3 lg:py-2 border border-gray-200 text-[10px] lg:text-xs font-medium flex items-center gap-0.5 lg:gap-1.5 ${sizePopupOpen ? 'bg-gray-900 text-white' : 'bg-white text-gray-800 hover:bg-gray-100'}`}
              >
                <Ruler size={11} className="lg:hidden" />
                <Ruler size={14} className="hidden lg:block" />
                <span>{km2} km²</span>
              </button>
              {sizePopupOpen && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-3 space-y-3 z-[1100]">
                  <div className="flex items-baseline gap-2 justify-center">
                    <input
                      type="number"
                      min={1}
                      value={km2}
                      onChange={(e) => {
                        const v = e.target.value === '' ? '' : Number(e.target.value);
                        if (v === '' || (Number.isFinite(v) && v > 0)) onKm2Change(v);
                      }}
                      className="w-28 text-center text-lg border-2 border-gray-200 focus:border-blue-500 rounded-xl py-1 outline-none"
                    />
                    <span className="text-xs text-gray-600">km²</span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={20000}
                    step={1}
                    value={Math.min(Number(km2) || 1, 20000)}
                    onChange={(e) => onKm2Change(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {[10, 50, 100, 500, 1000, 5000, 10000].map((preset) => (
                      <Button
                        key={preset}
                        type="button"
                        onClick={() => onKm2Change(preset)}
                        className={`text-xs px-2.5 py-1 rounded-full border h-auto ${Number(km2) === preset
                          ? 'bg-gray-900 text-white border-gray-900'
                          : 'bg-white border-gray-300 hover:bg-gray-100 text-gray-800'}`}
                        variant="ghost"
                      >
                        {preset}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {onFormatSelect && (
            <div className="relative" ref={formatRef}>
              <button
                type="button"
                onClick={() => {
                  setFormatPopupOpen((v) => !v);
                  setSizePopupOpen(false);
                }}
                title="Change format"
                className={`rounded shadow-md px-1.5 py-0.5 lg:px-3 lg:py-2 border border-gray-200 text-[10px] lg:text-xs font-medium flex items-center gap-0.5 lg:gap-1.5 ${formatPopupOpen ? 'bg-gray-900 text-white' : 'bg-white text-gray-800 hover:bg-gray-100'}`}
              >
                <LayoutGrid size={11} className="lg:hidden" />
                <LayoutGrid size={14} className="hidden lg:block" />
                <span>
                  {selectedFormat?.value === 'custom'
                    ? (selectedFormat.label || 'Custom').trim()
                    : (selectedFormat?.value || 'Format')}
                </span>
              </button>
              {formatPopupOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 p-3 space-y-3 z-[1100]">
                  <div className="grid grid-cols-2 gap-2">
                    {Object.keys(FORMATS).map((format) => {
                      const [w, h] = FORMATS[format].S.split('x').map((d) =>
                        parseFloat(d.replace(',', '.'))
                      );
                      const display = isPortrait ? `${w}×${h}` : `${h}×${w}`;
                      const isActive = selectedFormat?.value === format;
                      return (
                        <Button
                          key={format}
                          type="button"
                          onClick={() => {
                            const dims = {
                              width: w,
                              height: h,
                              originalWidth: isPortrait ? h : w,
                              originalHeight: isPortrait ? w : h,
                            };
                            onFormatSelect({
                              value: format,
                              size: 'S',
                              label: ` ${format}`,
                              dimensions: dims,
                              aspectRatio: dims.width / dims.height,
                            });
                          }}
                          className={`p-2 border rounded-xl text-center transition-colors h-auto ${isActive
                            ? 'border-blue-500 bg-blue-50 text-gray-900'
                            : 'border-gray-200 hover:bg-gray-50 bg-white text-gray-900'}`}
                          variant="ghost"
                        >
                          <div className="font-medium text-sm">{format}</div>
                        </Button>
                      );
                    })}
                  </div>
                  {onTogglePortrait && (
                    <div className="flex items-center justify-center pt-2 border-t border-gray-100">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isPortrait}
                          onChange={onTogglePortrait}
                          className="h-4 w-4"
                        />
                        Portrait
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Activity finder trigger — only rendered when the activity
                overlay is enabled and at least one source is selected, since
                there's nothing to find otherwise. */}
          {showActivityPolylines && activitySources.length > 0 && (
            <div className="relative" ref={finderRef}>
              <button
                type="button"
                onClick={() => {
                  setFinderOpen((v) => !v);
                  setFinderTypeFilter(null);
                  setFinderQuery('');
                }}
                title="Center on activity"
                className={`rounded shadow-md px-1.5 py-0.5 lg:px-3 lg:py-2 border border-gray-200 text-[10px] lg:text-xs font-medium flex items-center gap-0.5 lg:gap-1.5 ${finderOpen ? 'bg-gray-900 text-white' : 'bg-white text-gray-800 hover:bg-gray-100'}`}
              >
                <Search size={11} className="lg:hidden" />
                <Search size={14} className="hidden lg:block" />
                <span>Activity</span>
              </button>
              {finderOpen && (
                <div className="absolute top-full right-0 mt-2 w-80 max-h-[28rem] bg-white rounded-xl shadow-xl border border-gray-200 flex flex-col overflow-hidden z-[1100]">
                  {renderFinderContent()}
                </div>
              )}
            </div>
          )}

          {/* Activity line-width control. The user-facing scale is 1–100
              for ergonomics, but the underlying value the renderer reads is
              0.01–5 — keeping it linear so the same number drives Leaflet
              weight, the matplotlib render, and the saved settings dict.
              See get_line_width() in backend/location_map.py for how the
              backend interprets the stored value.

              Two modes: auto (width tracks visible activity count via the
              suggestion curve) and manual (user-set, value sticks). The
              button label reflects the mode; the popup contents toggle
              between an info row and the manual controls. */}
          {showActivityPolylines && activitySources.length > 0 && onActivityLineWidthChange && (() => {
            const INTERNAL_MIN = 0.01;
            const INTERNAL_MAX = 5;
            const DISPLAY_MIN = 1;
            const DISPLAY_MAX = 100;
            const internalToDisplay = (v) => {
              const ratio = (v - INTERNAL_MIN) / (INTERNAL_MAX - INTERNAL_MIN);
              return Math.round(DISPLAY_MIN + ratio * (DISPLAY_MAX - DISPLAY_MIN));
            };
            const displayToInternal = (d) => {
              const ratio = (d - DISPLAY_MIN) / (DISPLAY_MAX - DISPLAY_MIN);
              const v = INTERNAL_MIN + ratio * (INTERNAL_MAX - INTERNAL_MIN);
              // Round to 2 decimals so settings JSON stays readable and the
              // saved value comes back identical after a round-trip.
              return Math.round(v * 100) / 100;
            };

            const raw = activitySettings?.lineWidth;
            // Accept legacy named widths for back-compat with older saved
            // settings; everything else falls back to a sane new-user default.
            const current = typeof raw === 'number'
              ? raw
              : raw === 'very thin' ? 0.5
                : raw === 'thin' ? 0.75
                  : raw === 'thick' ? 3
                    : raw === 'very thick' ? 5
                      : raw === 'very very thick' ? 7
                        : 0.8;
            const currentDisplay = internalToDisplay(
              Math.max(INTERNAL_MIN, Math.min(INTERNAL_MAX, current))
            );

            // Suggested width tracks how busy the map is: a single track
            // can be bold without losing context, while crowded views need
            // hairlines to keep individual paths distinguishable. The N^0.4
            // curve falls off slower than √N so 1000-activity maps still
            // get a usable thickness; 0.05 floor keeps lines visible. Count
            // is the unhidden polylines so panel toggles affect it.
            const visibleCount = visibleActivityCount;
            const suggested = visibleCount <= 0
              ? 0.8
              : Math.max(0.05, Math.round((2.4 / Math.pow(visibleCount, 0.4)) * 100) / 100);
            const suggestedDisplay = internalToDisplay(suggested);

            const setInternal = (v) => {
              const clamped = Math.max(INTERNAL_MIN, Math.min(INTERNAL_MAX, v));
              onActivityLineWidthChange(Math.round(clamped * 100) / 100);
            };
            // Manual edits drop out of auto mode so the user's choice sticks.
            const setManualValue = (v) => {
              if (lineWidthAuto && onActivityLineWidthAutoChange) {
                onActivityLineWidthAutoChange(false);
              }
              setInternal(v);
            };
            const enableAuto = () => {
              if (onActivityLineWidthAutoChange) onActivityLineWidthAutoChange(true);
              setInternal(suggested);
            };

            return (
              <div className="relative" ref={lineWidthRef}>
                <button
                  type="button"
                  onClick={() => setLineWidthPopupOpen((v) => !v)}
                  title={lineWidthAuto ? "Auto width — click to change" : "Adjust activity line width"}
                  className={`rounded shadow-md px-1.5 py-0.5 lg:px-3 lg:py-2 border border-gray-200 text-[10px] lg:text-xs font-medium flex items-center gap-0.5 lg:gap-1.5 ${lineWidthPopupOpen ? 'bg-gray-900 text-white' : 'bg-white text-gray-800 hover:bg-gray-100'}`}
                >
                  <Minus size={11} className="lg:hidden" strokeWidth={Math.min(4, Math.max(1, current))} />
                  <Minus size={14} className="hidden lg:block" strokeWidth={Math.min(4, Math.max(1, current))} />
                  <span>{lineWidthAuto ? 'Width Auto' : `Width ${currentDisplay}`}</span>
                </button>
                {lineWidthPopupOpen && (
                  <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-3 space-y-3 z-[1100]">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-medium text-gray-700">Activity line width</div>
                      {onActivityLineWidthAutoChange && (
                        lineWidthAuto ? (
                          <button
                            type="button"
                            onClick={() => onActivityLineWidthAutoChange(false)}
                            className="text-[11px] px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-100"
                          >
                            Switch to manual
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={enableAuto}
                            className="text-[11px] px-2 py-0.5 rounded border border-gray-900 bg-gray-900 text-white hover:bg-gray-700"
                          >
                            Auto
                          </button>
                        )
                      )}
                    </div>
                    {lineWidthAuto ? (
                      <div className="text-[11px] text-gray-600">
                        Width is set automatically based on how many activities are visible.
                        {visibleCount > 0 && (
                          <> Currently <span className="font-semibold text-gray-800">{suggestedDisplay}</span> for {visibleCount} {visibleCount === 1 ? 'activity' : 'activities'}.</>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <input
                            type="range"
                            min={DISPLAY_MIN}
                            max={DISPLAY_MAX}
                            step={1}
                            value={currentDisplay}
                            onChange={(e) => setManualValue(displayToInternal(parseInt(e.target.value, 10)))}
                            className="flex-1 min-w-0"
                          />
                          <input
                            type="number"
                            min={DISPLAY_MIN}
                            max={DISPLAY_MAX}
                            step={1}
                            value={currentDisplay}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10);
                              if (!isNaN(n) && n >= DISPLAY_MIN && n <= DISPLAY_MAX) {
                                setManualValue(displayToInternal(n));
                              }
                            }}
                            className="w-14 shrink-0 text-center border border-gray-200 rounded h-7 text-xs"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-1">
                          <button
                            type="button"
                            onClick={() => setManualValue(1.2)}
                            className="text-[11px] px-2 py-1 rounded border border-gray-200 hover:bg-gray-100"
                            title="Best for 1 activity"
                          >
                            Single
                          </button>
                          <button
                            type="button"
                            onClick={() => setManualValue(0.3)}
                            className="text-[11px] px-2 py-1 rounded border border-gray-200 hover:bg-gray-100"
                            title="Good for ~10 activities"
                          >
                            Some
                          </button>
                          <button
                            type="button"
                            onClick={() => setManualValue(0.05)}
                            className="text-[11px] px-2 py-1 rounded border border-gray-200 hover:bg-gray-100"
                            title="Best for 100+ activities"
                          >
                            Many
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

        </div>

        {/* Zoom-to-rectangle: sits just below leaflet's built-in +/- zoom
            control (top-left, ~70px below the top edge). */}
        <button
          type="button"
          onClick={handleZoomToRectangle}
          title="Zoom to rectangle"
          aria-label="Zoom to rectangle"
          className="absolute top-[78px] left-[10px] z-[1000] bg-white hover:bg-gray-100 rounded shadow-md p-1 lg:p-2 border border-gray-200"
        >
          <RectangleHorizontalIcon size={14} className="text-blue-600 lg:hidden" />
          <RectangleHorizontalIcon size={18} className="text-blue-600 hidden lg:block" />
        </button>

        {/* Top-center: location search trigger + popup. Lets the user change
            the map's place without leaving the map. */}
        {onPlaceNameChange && (
          <div ref={locationRef} className="absolute top-2 lg:top-4 left-1/2 -translate-x-1/2 z-[1000] flex items-center">
            {/* Location pill: left region opens the search popup, right
                region (only when a place is set) centers the map on that
                place. Separator keeps the two actions visually distinct. */}
            <div className={`flex items-stretch rounded shadow-md border border-gray-200 overflow-hidden max-w-[200px] lg:max-w-[260px] ${locationPopupOpen ? 'bg-gray-900' : 'bg-white'}`}>
              <button
                type="button"
                onClick={() => setLocationPopupOpen((v) => !v)}
                title="Search location"
                className={`flex items-center gap-1 px-1.5 py-0.5 lg:px-3 lg:py-2 text-[10px] lg:text-xs font-medium ${locationPopupOpen ? 'text-white hover:bg-gray-800' : 'text-gray-800 hover:bg-gray-100'}`}
              >
                <MapPin size={12} className="lg:hidden" />
                <MapPin size={14} className="hidden lg:block" />
                <span className="truncate">
                  {/* Show the active placeName only while the user is mid-
                      selection (still typing OR they've picked a result and
                      not yet centered). After the center button has been
                      clicked the visible label resets to "Search location",
                      even though placeName itself stays around for the
                      print label and recent_images entry. */}
                  {(pendingCenter || searchInput) && placeName ? placeName : 'Search location'}
                </span>
              </button>
              {onCenterPlace && placeName && (
                <>
                  <div className={`w-px ${locationPopupOpen ? 'bg-gray-700' : pendingCenter ? 'bg-blue-700' : 'bg-gray-300'}`} />
                  <button
                    type="button"
                    onClick={() => {
                      onCenterPlace();
                      setPendingCenter(false);
                      // Drop the in-flight search query so the next time
                      // the user opens the popup they start clean.
                      setSearchInput('');
                    }}
                    title="Center map on this place"
                    aria-label="Center map on this place"
                    className={`flex items-center px-1.5 lg:px-2 transition-colors ${pendingCenter
                      ? 'bg-blue-600 text-white hover:bg-blue-700 animate-pulse'
                      : locationPopupOpen
                        ? 'text-blue-300 hover:bg-gray-800'
                        : 'text-blue-600 hover:bg-gray-100'
                      }`}
                  >
                    <ArrowRight size={12} className="lg:hidden" />
                    <ArrowRight size={14} className="hidden lg:block" />
                  </button>
                </>
              )}
            </div>
            {locationPopupOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 p-3 space-y-3 z-[1100]">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => {
                      setSearchInput(e.target.value);
                      onPlaceNameChange(e);
                    }}
                    placeholder="Search a place…"
                    autoFocus
                    className={`w-full h-10 pl-9 border-2 border-gray-200 focus:border-blue-500 bg-gray-50 focus:bg-white rounded-xl text-sm outline-none ${(searchInput || placeName) ? 'pr-9' : 'pr-3'}`}
                  />
                  {(searchInput || placeName) && onClearPlace && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setSearchInput(''); onClearPlace(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-200"
                      aria-label="Clear location"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                {searchResults.length > 0 && (
                  <div className="border border-gray-200 rounded-xl max-h-52 overflow-y-auto">
                    {searchResults.map((result, index) => (
                      <div
                        key={index}
                        className="px-3 py-1.5 text-sm hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 truncate"
                        title={result.display_name}
                        onClick={() => {
                          if (onSelectResult) onSelectResult(result);
                          setPendingCenter(true);
                          setLocationPopupOpen(false);
                        }}
                      >
                        {result.display_name}
                      </div>
                    ))}
                  </div>
                )}

                {recentPlaces.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">Recent</div>
                    <div className="flex flex-wrap gap-1.5">
                      {recentPlaces.map((name) => (
                        <Button
                          key={name}
                          type="button"
                          onClick={() => {
                            if (onSelectRecent) onSelectRecent(name);
                            setPendingCenter(true);
                            setLocationPopupOpen(false);
                          }}
                          className="px-2.5 py-1 text-xs rounded-full border border-gray-300 bg-white hover:bg-gray-100 text-gray-800 h-auto truncate max-w-full"
                          title={name}
                          variant="ghost"
                        >
                          {name.split(',')[0]}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}


        {/* Style switcher — bottom-left so it doesn't overlap Leaflet's top-left zoom controls. */}
        {onMapStyleChange && (
          <div className="absolute bottom-2 left-2 lg:bottom-4 lg:left-4 bg-white rounded shadow-md flex text-xs z-[1000] overflow-hidden">
            {[
              { id: 'standard', label: 'Map' },
              { id: 'topo', label: 'Topo' },
              { id: 'satellite', label: 'Satellite' },
            ].map(option => (
              <button
                key={option.id}
                type="button"
                onClick={() => onMapStyleChange(option.id)}
                className={`px-2 py-0.5 lg:px-3 lg:py-1 text-[10px] lg:text-xs border-r last:border-r-0 ${activeStyle === option.id ? 'bg-gray-900 text-white' : 'hover:bg-gray-100'}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MapComponent;
